<template>
  <div>
    <validation-observer ref="simpleRules">
      <b-card>
        <b-form @submit.prevent>
          <b-row>
            <b-col
              cols="12"
              lg="6"
              class="mb-2"
            >
              <b-card class="border h-100 mb-0">
                <div class="d-flex align-items-center mb-2">
                  <feather-icon
                    icon="UserIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('myAccount.sections.generalInformation') }}
                  </h5>
                </div>

                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('myAccount.fields.prefix')"
                      label-for="mc-prefix"
                    >
                      <b-form-input
                        id="mc-prefix"
                        v-model="form.prefix"
                        :placeholder="$t('myAccount.placeholders.prefix')"
                        readonly
                      />
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group
                      :label="$t('myAccount.fields.username')"
                      label-for="mc-username"
                    >
                      <b-form-input
                        id="mc-username"
                        v-model="form.username"
                        :placeholder="$t('myAccount.placeholders.username')"
                        readonly
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>

            <b-col
              cols="12"
              lg="6"
              class="mb-2"
            >
              <b-card class="border h-100 mb-0">
                <div class="d-flex align-items-center mb-2">
                  <feather-icon
                    icon="GlobeIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('myAccount.sections.apiSettings') }}
                  </h5>
                </div>

                <b-row>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('myAccount.fields.websiteUrl')"
                      label-for="mc-website-url"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('myAccount.fields.websiteUrl')"
                        rules="required|url"
                      >
                        <b-form-input
                          id="mc-website-url"
                          v-model="form.websiteUrl"
                          placeholder="https://example.com"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12">
                    <b-form-group
                      :label="$t('myAccount.fields.callbackUrl')"
                      label-for="mc-callback-url"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('myAccount.fields.callbackUrl')"
                        rules="required|url"
                      >
                        <b-form-input
                          id="mc-callback-url"
                          v-model="form.callbackUrl"
                          placeholder="https://example.com/callback"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>

                  <b-col cols="12">
                    <b-form-group
                      :label="$t('myAccount.fields.secretKey')"
                      label-for="mc-secret-key"
                    >
                      <b-form-input
                        id="mc-secret-key"
                        v-model="form.secretKey"
                        :placeholder="$t('myAccount.fields.secretKey')"
                        readonly
                      />
                    </b-form-group>
                  </b-col>

                  <b-col cols="12">
                    <b-form-group
                      :label="$t('myAccount.fields.ipWhitelist')"
                      label-for="mc-ip-whitelist"
                      class="mb-0"
                    >
                      <div class="ip-whitelist-inline">
                        <div
                          v-for="(ip, index) in form.ipWhiteList"
                          :key="index"
                          class="ip-whitelist-tag"
                        >
                          <validation-provider
                            #default="{ errors }"
                            :name="`IP ${index + 1}`"
                            rules="regex:^\d+\.\d+\.\d+\.\d+$"
                          >
                            <b-input-group>
                              <b-form-input
                                v-model="form.ipWhiteList[index]"
                                :placeholder="$t('IP Address')"
                                :state="errors.length > 0 ? false : null"
                              />
                              <b-input-group-append>
                                <b-button
                                  variant="outline-danger"
                                  @click="removeIpItem(index)"
                                >
                                  <feather-icon icon="XIcon" />
                                </b-button>
                              </b-input-group-append>
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </div>

                        <b-button
                          v-ripple.400="'var(--hdg-ripple-primary)'"
                          variant="outline-primary"
                          class="btn-icon"
                          @click="addIpItem"
                        >
                          <feather-icon icon="PlusIcon" />
                        </b-button>
                      </div>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>

          <div class="text-right">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              @click.prevent="onSubmitForm"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              {{ $t('myAccount.actions.saveChanges') }}
            </b-button>
          </div>
        </b-form>
      </b-card>
    </validation-observer>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BForm,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {} from '@validations'
import helper from '@/service/helper'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BForm,
    BButton,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      form: {
        prefix: '',
        username: '',
        websiteUrl: '',
        callbackUrl: '',
        secretKey: '',
        ipWhiteList: [''],
      },
    }
  },
  async mounted() {
    const res = await helper.getAuthen('agent/profile')
    if (res.status === 200) {
      this.form = res.data
      if (!Array.isArray(this.form.ipWhiteList)) {
        this.form.ipWhiteList = []
      }
    }
  },
  methods: {
    addIpItem() {
      this.form.ipWhiteList.push('')
    },
    removeIpItem(index) {
      this.form.ipWhiteList.splice(index, 1)
    },
    onSubmitForm() {
      this.$refs.simpleRules.validate()
        .then(async success => {
          if (success) {
            const formSender = { ...this.form }
            delete formSender.passwordConfirm
            const res = await helper.putAuthen('agent/profile/information', formSender)
            if (res.status === 200) {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: this.$t('myAccount.messages.success'),
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: res.data,
                },
              })
            } else {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: this.$t('myAccount.messages.error'),
                  icon: 'AlertCircleIcon',
                  variant: 'danger',
                  text: res.data,
                },
              })
            }
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@media (max-width: 767px) {
  .card-body {
    padding: 1rem;
  }
}

.ip-whitelist-inline {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.75rem;

  .ip-whitelist-tag {
    flex: 1 1 260px;
    max-width: 100%;
  }
}
</style>
